// The price box's reader (N39) — what a supplier types into "What you get
// paid", and a hotel into its invite form, turned into integer minor units and
// never through a float (the money rule: integers in minor units at every layer;
// `Number.parseFloat("58,50")` is 58, and it must never reach a save).
//
// One rule, the API's: this COPIES the figure rule Eva reads a typed price by —
// `readFigure` in api/src/domain/evaSetup.ts — cut down to a box that holds one
// figure. Two clients, two copies (this file and app/src/price.ts), kept in
// step by hand, as joining.ts's words are; each is tested beside it
// (price.test.ts). The API still validates the integer it is sent
// (domain/product.ts), so this only reads, it decides nothing.
//
//   "58", "€ 58", "58 euro"  € 58.00
//   "58,50", "58,5"          € 58.50   a comma is the decimal point (Greek)
//   "58.50"                  € 58.50
//   "1.200,50", "1,200.50"   € 1,200.50  two separators: thousands, then decimals
//   "1 200", "12 500,50"     thousands written with spaces
//   "1.200", "1,200"         two readings — € 1,200.00 or € 1.20 — asked, both in full
//   "1.255"                  € 1,255.00  (as a decimal it is no price)
//   "1.200.000"              € 1,200,000.00
//   a figure too large to hold exactly (past 2^53 minor units) — not a price
//   anything else            not a price
//
// Zero is read as € 0.00 and left to the screen, as before: the phone's trip
// sheet refuses it, the desk's trip form takes it (≥ 0, as it always has), and
// Eva refuses it (she reads more than € 0.00, up to € 100,000.00). The API takes any
// non-negative safe integer.
//
// The words below copy the API's `formatMoney` (domain/money.ts): the cents from
// the integer, the currency always shown — "€ 1,200.50". Since the N39 review
// they are the client's ONE money formatter (hard constraint 7): ui.tsx
// `fmtMoney` returns them.

export type PriceRead =
  | { kind: 'empty' }
  | { kind: 'one'; minor: number }
  | { kind: 'two'; choices: [number, number] }
  | { kind: 'bad' };

const int = (s: string): number => Number.parseInt(s, 10);
const centsOf = (d: string): number => (d.length === 1 ? int(d) * 10 : int(d));
const MARK = /^(?:€|eur|euros?|ευρώ|ευρω)\s*|\s*(?:€|eur|euros?|ευρώ|ευρω)$/giu;
const SPACED = /^\d{1,3}(?:[   ]\d{3})+(?:[.,]\d{1,2})?$/;

function figure(s: string): PriceRead {
  if (SPACED.test(s)) return figure(s.replace(/[   ]/g, ''));
  if (!/^\d[\d.,]*$/.test(s) || /[.,]$/.test(s)) return { kind: 'bad' };
  const seps = s.match(/[.,]/g) ?? [];
  if (!seps.length) return { kind: 'one', minor: int(s) * 100 };
  const last = Math.max(s.lastIndexOf('.'), s.lastIndexOf(','));
  if (new Set(seps).size === 2) {
    const dec = s.slice(last + 1);
    const whole = s.slice(0, last);
    const thousands = s[last] === '.' ? ',' : '.';
    if (!/^\d{1,2}$/.test(dec)) return { kind: 'bad' };
    if (!new RegExp(`^\\d{1,3}(?:\\${thousands}\\d{3})+$`).test(whole)) return { kind: 'bad' };
    return { kind: 'one', minor: int(whole.replace(/[.,]/g, '')) * 100 + centsOf(dec) };
  }
  const groups = s.split(/[.,]/);
  if (groups.length > 2) {
    if (!/^\d{1,3}$/.test(groups[0]!) || !groups.slice(1).every((g) => /^\d{3}$/.test(g))) return { kind: 'bad' };
    return { kind: 'one', minor: int(groups.join('')) * 100 };
  }
  const [a, b] = groups as [string, string];
  if (/^\d{1,2}$/.test(b)) return { kind: 'one', minor: int(a) * 100 + centsOf(b) };
  if (/^\d{3}$/.test(b)) {
    const asThousands = int(a + b) * 100;
    // As a decimal it is a price only when the third digit is 0: "1.200" is
    // € 1.20 or € 1,200.00; "1.255" can only be € 1,255.00.
    if (b.endsWith('0') && a.length <= 3) {
      const asDecimal = int(a) * 100 + int(b.slice(0, 2));
      return asDecimal === asThousands ? { kind: 'one', minor: asThousands } : { kind: 'two', choices: [asThousands, asDecimal] };
    }
    return { kind: 'one', minor: asThousands };
  }
  return { kind: 'bad' };
}

/** What a price box holds, as minor units — or the two figures it could be, to ask which. */
export function readPriceBox(text: string): PriceRead {
  const t = text.trim();
  if (!t) return { kind: 'empty' };
  const bare = t.replace(MARK, '').trim();
  if (!bare) return { kind: 'bad' };
  const read = figure(bare);
  // A figure past 2^53 is not the one typed: refused, never shown rounded.
  if (read.kind === 'one' && !Number.isSafeInteger(read.minor)) return { kind: 'bad' };
  if (read.kind === 'two' && !read.choices.every((m) => Number.isSafeInteger(m))) return { kind: 'bad' };
  return read;
}

/** The figure a box holds, in minor units: its one reading, or the reading chosen of two; else undefined. */
export function chosenMinor(text: string, choice: number | null): number | undefined {
  const read = readPriceBox(text);
  if (read.kind === 'one') return read.minor;
  if (read.kind === 'two' && choice !== null && read.choices.includes(choice)) return choice;
  return undefined;
}

/** Money in words, as the API writes it (domain/money.ts formatMoney): "€ 1,200.50". */
export function moneyWords(minor: number, currency: string): string {
  const abs = Math.abs(minor);
  const whole = Math.floor(abs / 100).toLocaleString('en-GB');
  const cents = String(abs % 100).padStart(2, '0');
  const v = `${minor < 0 ? '-' : ''}${whole}.${cents}`;
  return currency === 'EUR' ? `€ ${v}` : `${currency} ${v}`;
}

/**
 * The same words with the sign and the figure held together (a no-break space),
 * for a figure inside running text: a line never breaks between "€" and "58.00"
 * (N39 review, principle 11). Display only — `moneyWords` stays the API's words.
 */
export function moneyInText(minor: number, currency: string): string {
  return moneyWords(minor, currency).replace(' ', ' ');
}

/** A price box's figure for an editor to start from: "58.50" — the box reads it back the same. */
export function priceBoxText(minor: number): string {
  return `${Math.floor(minor / 100)}.${String(minor % 100).padStart(2, '0')}`;
}

/**
 * How long a trip takes, as typed: "6", "1,5", "1.5", "2 h", "3 hours". Hours
 * are a duration, not money, so a decimal is fine; the API takes 0–24.
 * Undefined when it is not a number of hours.
 */
export function readHours(text: string): number | undefined {
  const m = /^\s*(\d{1,2})(?:[.,](\d{1,2}))?\s*(?:h|hrs?|hours?)?\s*$/i.exec(text);
  if (!m) return undefined;
  const hours = int(m[1]!) + (m[2] ? int(m[2]) / 10 ** m[2].length : 0);
  return hours > 0 && hours <= 24 ? hours : undefined;
}

/** The most guests a trip takes, as typed: a whole number, 1 to 1000. */
export function readGuests(text: string): number | undefined {
  const m = /^\s*(\d{1,4})\s*$/.exec(text);
  if (!m) return undefined;
  const n = int(m[1]!);
  return n >= 1 && n <= 1000 ? n : undefined;
}
